// apps/client/src/services/chapter.ts
import {
  BulkReorderChaptersRequest,
  Chapter,
  ChapterCreateResponse,
  ChapterGetResponse,
  ChapterListResponse,
  ChapterUpdateResponse,
  NewChapter,
  Page,
  UpdateChapter,
} from '@fsp/types';
import { apiClient } from './apiClient';

export const fetchChapters = async (
  organisationId: string,
  planId: string,
  signal?: AbortSignal
): Promise<Chapter[]> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/chapters`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch chapters: ${response.statusText}`);
  }

  const result: ChapterListResponse = await response.json();
  return result.chapters || [];
};

export const fetchChapter = async (
  organisationId: string,
  planId: string,
  chapterId: string,
  signal?: AbortSignal
): Promise<{ chapter: Chapter; pages: Page[] }> => {
  const response = await apiClient.get(
    `/organisations/${organisationId}/plans/${planId}/chapters/${chapterId}`,
    signal
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch chapter: ${response.statusText}`);
  }

  const result: ChapterGetResponse = await response.json();
  return { chapter: result.chapter, pages: result.pages };
};

export const createChapter = async (
  organisationId: string,
  planId: string,
  data: NewChapter,
  signal?: AbortSignal
): Promise<Chapter> => {
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/chapters`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to create chapter: ${response.statusText}`);
  }

  const result: ChapterCreateResponse = await response.json();
  return result.chapter;
};

export const updateChapter = async (
  organisationId: string,
  planId: string,
  chapterId: string,
  data: UpdateChapter,
  signal?: AbortSignal
): Promise<Chapter> => {
  const response = await apiClient.put(
    `/organisations/${organisationId}/plans/${planId}/chapters/${chapterId}`,
    data,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to update chapter: ${response.statusText}`);
  }

  const result: ChapterUpdateResponse = await response.json();
  return result.chapter;
};

export const deleteChapter = async (
  organisationId: string,
  planId: string,
  chapterId: string,
  signal?: AbortSignal
): Promise<void> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/plans/${planId}/chapters/${chapterId}`,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to delete chapter: ${response.statusText}`);
  }
};

// Bulk reorder chapters - single API call
export const reorderChaptersBulk = async (
  organisationId: string,
  planId: string,
  chapters: { id: string; sortOrder: number }[],
  signal?: AbortSignal
): Promise<void> => {
  const requestData: BulkReorderChaptersRequest = { chapters };
  const response = await apiClient.post(
    `/organisations/${organisationId}/plans/${planId}/chapters/reorder`,
    requestData,
    signal
  );

  if (!response.ok) {
    const errorResult = await response.json();
    throw new Error(errorResult.message || `Failed to reorder chapters: ${response.statusText}`);
  }
};

// @deprecated Use reorderChaptersBulk instead - this makes N API calls
export const reorderChapters = async (
  organisationId: string,
  planId: string,
  chapters: { id: string; sortOrder: number }[]
): Promise<void> => {
  await Promise.all(
    chapters.map((chapter) =>
      updateChapter(organisationId, planId, chapter.id, {
        sortOrder: chapter.sortOrder,
      })
    )
  );
};
